// Two-way anchoring between the chat thread and the Context Explorer: an inline resource marker
// carries `data-resource-key`, an explorer row carries `data-resource-row`, both keyed by
// resourceKey. Each side can scroll to + briefly flash the other.

const REVEAL_HIGHLIGHT_MS = 1400;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const scrollBehavior = (): ScrollBehavior => (prefersReducedMotion() ? 'auto' : 'smooth');

// `highlightClass` picks the flash's settle color: markers rest on bg-secondary (…-secondary),
// explorer rows rest transparent (the default). Fading to the wrong one flashes to white behind.
const flash = (el: HTMLElement, highlightClass = 'resource-reveal-highlight') => {
  // Retrigger cleanly if the element is still mid-flash from a previous reveal.
  el.classList.remove(highlightClass);
  void el.offsetWidth;
  el.classList.add(highlightClass);
  setTimeout(() => el.classList.remove(highlightClass), REVEAL_HIGHLIGHT_MS);
};

// Scroll the chat thread to the anchor for this resource and flash it. Returns false
// only when no anchor is currently in the thread, so the caller can fall back to opening the resource.
export const scrollToResourceMarker = (key: string): boolean => {
  const el = document.querySelector<HTMLElement>(`[data-resource-key="${CSS.escape(key)}"]`);
  if (!el) return false;
  el.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
  flash(el, 'resource-reveal-highlight-secondary');
  return true;
};

/** Scroll the Context Explorer to a resource's row and flash it. */
export const flashResourceRow = (key: string) => {
  const el = document.querySelector<HTMLElement>(`[data-resource-row="${CSS.escape(key)}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: scrollBehavior(), block: 'nearest' });
  flash(el);
};

// Tiny event bus so an inline marker can ask the Context Explorer (a sibling subtree) to open the
// folder holding a resource and flash its row — the explorer reacts via subscribeRevealResource.
type RevealListener = (key: string) => void;
const revealListeners = new Set<RevealListener>();

export const revealResourceInExplorer = (key: string) => {
  for (const listener of revealListeners) listener(key);
};

export const subscribeRevealResource = (listener: RevealListener): (() => void) => {
  revealListeners.add(listener);
  return () => {
    revealListeners.delete(listener);
  };
};
